import { AxiosResponse } from 'axios';

import {
	RecoverPasswordProps,
	ResetPasswordProps,
	Login2FAProps,
	LoginProps,
	RefreshTokenBody,
	Verify2FAProps,
	SendWithdrawalRequestProps,
	DownloadUserAgreementProps,
	AcceptAgreementProps,
	GetLastNews, LoginOTPProps,
} from 'models/api';

import BaseApi from './BaseApi';
import { language } from '../utils';

class AccountApi extends BaseApi {
	constructor() {
		super('/investor');
	}

	login(body: LoginProps): Promise<AxiosResponse<unknown>> {
		return this.httpClient.post({ url: '/login', body: {
			email: body.credentials,
			password: body.password
		}});
	}

	login2FA(body: Login2FAProps): Promise<AxiosResponse<unknown>> {
		return this.httpClient.post({ body, url: `/login/2fa` });
	}

	loginOtp(body: LoginOTPProps): Promise<AxiosResponse<unknown>> {
		return this.httpClient.post({ body, url: `/login/email-code` });
	}

	logout() {
		return this.httpClient.get({ url: '/logout' });
	}

	verify(): Promise<AxiosResponse<unknown>> {
		return this.httpClient.get({ url: `/verification` });
	}

	getBalance(): Promise<AxiosResponse<unknown>> {
		return this.httpClient.get({ url: '/balance/main', conditions: { auth: true, version: 'v2' } });
	}

	refreshToken(body: RefreshTokenBody): Promise<AxiosResponse<RefreshTokenBody>> {
		return this.httpClient.post({ url: `/refresh-token`, body });
	}

	verify2FA(body: Verify2FAProps): Promise<AxiosResponse<unknown>> {
		return this.httpClient.post({
			url: `/2fa/toggle`,
			body,
			conditions: { auth: true },
		});
	}

	profile(): Promise<AxiosResponse<unknown>> {
		return this.httpClient.get({ conditions: { version: 'v2', auth: true }, url: '/profile' });
	}

	getQRCode(): Promise<AxiosResponse<unknown>> {
		return this.httpClient.get({ conditions: { auth: true }, url: '/2fa/secret' });
	}

	loyalty(): Promise<AxiosResponse<unknown>> {
		return this.httpClient.get({ conditions: { auth: true }, url: '/loyalty' });
	}

	deposit() {
		return this.httpClient.get({ conditions: { auth: true, version: 'v2' }, url: '/deposit' });
	}

	withdrawals() {
		return this.httpClient.get({ conditions: { auth: true }, url: '/withdrawals' });
	}

	sendWithdrawalRequest(body: SendWithdrawalRequestProps) {
		return this.httpClient.post({ conditions: { auth: true }, url: '/withdrawals', body });
	}

	downloadAgreement({ agreement }: DownloadUserAgreementProps) {
		return this.httpClient.get({
			conditions: { auth: true },
			url: `/agreement/${agreement}/${language}`,
			config: {
				responseType: 'blob',
			},
		});
	}

	resetPassword(body: ResetPasswordProps) {
		return this.httpClient.post({ url: `/reset-password`, body });
	}

	recoveryPassword(body: RecoverPasswordProps) {
		return this.httpClient.post({ url: `/recovery-password`, body });
	}

	checkResetPasswordToken(token: string) {
		return this.httpClient.get({ url: `/reset-password?password_token=${token}` });
	}

	deleteAccountRequest() {
		return this.httpClient.get({
			url: '/remove-request',
			conditions: { auth: true },
		});
	}

	deleteAccountConfirm(body: any) {
		return this.httpClient.post({ url: `/confirm-account-deletion`, body });
	}

	sendInvestmentRisksAgree() {
		return this.httpClient.post({
			conditions: { version: 'v2', auth: true },
			url: '/investment-risks/agree',
		});
	}

	saveRiskTolerance(body: { value: string }) {
		return this.httpClient.post({ url: '/risk-tolerance', body, conditions: { version: 'v2' } });
	}
	acceptAgreement(body: AcceptAgreementProps) {
		return this.httpClient.patch({
			conditions: { auth: true },
			url: '/agreement/accept',
			body,
		});
	}

	getDocuments() {
		return this.httpClient.get({
			conditions: { auth: true, version: 'v2' },
			url: '/profile/documents',
		});
	}

	getAvailableCoupon(params: { code: string }) {
		return this.httpClient.get({
			conditions: { auth: true, version: 'v2' },
			url: '/coupon/available',
			config: { params },
		});
	}

	getSummary(params: { startDate: string; endDate: string }) {
		return this.httpClient.get({
			conditions: { auth: true, version: 'v2' },
			url: '/account-summary',
			config: { params },
		});
	}

	approveWithdrawal(body: { code: string }) {
		return this.httpClient.post({
			conditions: { auth: true },
			url: '/withdrawals/approve',
			body,
		});
	}

	getLastNewsOfProject({ id, lang, params }: GetLastNews) {
		return this.httpClient.get({
			conditions: { auth: true, version: 'v2' },
			url: `/news/of-project/${id}/${lang}`,
			config: { params },
		});
	}

	getActiveSessions() {
		return this.httpClient.get({ conditions: { auth: true, version: 'v2' }, url: '/sessions' });
	}

	removeActiveSessions(session_uuid: string) {
		return this.httpClient.delete({
			conditions: { auth: true, version: 'v2' },
			url: '/session',
			body: { data: { session_uuid } }
		});
	}

	removeAllActiveSessions() {
		return this.httpClient.delete({ conditions: { auth: true, version: 'v2' }, url: '/sessions' });
	}
}

export const AccountService = new AccountApi();
